'use client';

import { useTranslations } from 'next-intl';
import { type ComponentProps, Fragment, type MouseEvent, useCallback, useEffect, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineList } from '@/app/components/icons/BaseLineList';
import { ChevronDown } from '@/app/components/icons/ChevronDown';
import { ViduSLogo } from '@/app/components/icons/ViduSLogo';
import { ClientLink } from '@/app/components/shared/ClientLink';
import {
  buildEditingModeHref,
  EDITING_MODE_DISPLAY_ORDER,
  EDITING_MODE_ROUTE_BY_ID,
  EDITING_MODE_ROUTES,
  type EditingMode,
  syncEditingModeToUrl,
  VIDU_STREAM_FEATURE_LINKS,
} from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStoreOptional } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { EDITING_CLICK_ELEMENT, reportEditingElementClick, resolveTrackedRefImageId } from '../registry/click-tracking';
import { useEditingStoreOptional } from '../store';
import { leaveActiveEditingLive } from '../utils/editing-leave-live';

type SidebarItem = { id: string; label: string; href: string; external?: boolean; modeId?: EditingMode };
type EditingSidebarProps = {
  hideProductHeader?: boolean;
  preserveLiveId?: boolean;
  /** Use the height of a parent workspace that already reserves the Stream header. */
  fitWorkspaceHeight?: boolean;
};

const linkClassName =
  'flex min-h-10 items-center rounded-8 px-3 pl-4 font-normal text-[13px] text-system-white64 transition-colors hover:bg-system-white08 hover:text-white';

const preserveEditingQuery = (href: string, preserveLiveId: boolean): string => {
  if (typeof window === 'undefined' || !href.startsWith('/vidu-stream/editing/')) return href;
  if (!preserveLiveId) return href;
  const liveId = new URLSearchParams(window.location.search).get('live_id')?.trim();
  return liveId ? `${href}?live_id=${encodeURIComponent(liveId)}` : href;
};

const isModifiedOrNonPrimaryClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

interface LeaveGuardedLinkProps extends ComponentProps<typeof ClientLink> {
  confirmLeave: boolean;
  onConfirmLeave: (href: string) => void;
}

/** 直播中点非 editing 内链时先确认，同意后再跳；新标签/修饰键点击不拦截。 */
const LeaveGuardedLink = ({ confirmLeave, onConfirmLeave, href, onClick, ...props }: LeaveGuardedLinkProps) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (confirmLeave && !isModifiedOrNonPrimaryClick(event)) {
      event.preventDefault();
      onClick?.(event);
      onConfirmLeave(String(href));
      return;
    }
    onClick?.(event);
  };

  return <ClientLink href={href} onClick={handleClick} {...props} />;
};

export const EditingSidebar = ({
  hideProductHeader = false,
  preserveLiveId = false,
  fitWorkspaceHeight = false,
}: EditingSidebarProps) => {
  const t = useTranslations('viduStreamNavigation');
  const userInfo = useStoreOptional((state) => state.userInfo);
  const pathname = usePathname();
  const router = useRouter();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const isEditingPage = pathname.startsWith('/vidu-stream/editing');
  const handleConfirmLeave = useCallback(
    (href: string) => {
      openConfirmDialog({
        title: t('leaveLiveConfirm.title'),
        content: t('leaveLiveConfirm.content'),
        secondaryButtonText: t('leaveLiveConfirm.cancel'),
        primaryButtonText: t('leaveLiveConfirm.confirm'),
        onPrimaryAction: () => {
          void (async () => {
            try {
              await leaveActiveEditingLive();
            } finally {
              router.push(href);
            }
          })();
        },
      });
    },
    [openConfirmDialog, router, t],
  );
  // sidebar 可能被挂在没有 EditingStoreProvider 的共享页面（如 /vidu-stream 落地页），
  // 那里读到的是兜底 store 的初始态（默认 mode），点击切换不影响任何真实工作区。
  const currentEditingMode = useEditingStoreOptional((state) => state.editingMode);
  const setEditingMode = useEditingStoreOptional((state) => state.setEditingMode);
  // 侧边栏在 editing 页之外也会渲染，这些字段仅用于点击埋点。
  // 读界面态而不是 editingVideoSource：后者要等用户确认输入才写，
  // 在输入页切到「视频」后它仍是 null，照读会把 video 报成 camera。
  const currentVideoSource = useEditingStoreOptional((state) => state.editingDisplayedVideoSource);
  const currentMaterials = useEditingStoreOptional((state) => state.editingMaterials);
  // 上报切换「后」的模式，素材要取目标模式自己的那份——素材是按模式分桶存的。
  const reportModeSwitch = (modeId: EditingMode) => {
    reportEditingElementClick({
      elementName: EDITING_CLICK_ELEMENT.switchEditingType,
      editingMode: modeId,
      videoSource: currentVideoSource,
      refImageId: resolveTrackedRefImageId(currentMaterials?.[modeId]),
    });
  };

  const groups: Array<{ id: string; label: string; items: SidebarItem[] }> = [
    {
      id: 'interaction',
      label: t('interaction.label'),
      items: [
        { id: 'onlineAvatar', label: t('interaction.onlineAvatar'), href: '/vidu-stream/avatar' },
        // 离线数字人入口暂时下线，后续恢复时取消注释。
        // { id: 'offlineAvatar', label: t('interaction.offlineAvatar'), href: '/vidu-stream/avatar/offline' },
        {
          id: 'interactionApi',
          label: t('apiPlatform'),
          href: VIDU_STREAM_FEATURE_LINKS.interaction.api,
          external: true,
        },
        {
          id: 'interactionDocs',
          label: t('documentation'),
          href: VIDU_STREAM_FEATURE_LINKS.interaction.usage,
          external: true,
        },
      ],
    },
    {
      id: 'editing',
      label: t('editing.label'),
      items: [
        ...EDITING_MODE_DISPLAY_ORDER.map((mode) => EDITING_MODE_ROUTE_BY_ID[mode]).map((entry) => ({
          id: entry.navigationKey,
          label: t(`editing.${entry.navigationKey}`),
          href: buildEditingModeHref(entry.id),
          modeId: entry.id,
        })),
        { id: 'editingApi', label: t('apiPlatform'), href: VIDU_STREAM_FEATURE_LINKS.editing.api, external: true },
        { id: 'editingDocs', label: t('documentation'), href: VIDU_STREAM_FEATURE_LINKS.editing.usage, external: true },
      ],
    },
  ];
  // 父子路由都可能匹配 pathname（如 /avatar 与 /avatar/offline），只选路径最长的具体菜单。
  // editing 组的 mode 项是单路由（URL 都是 /vidu-stream/editing），pathname 匹配不出具体 mode，
  // 所以该组 active 改由 store 的 editingMode 判定；其它组仍走 pathname。
  const activeItem = groups
    .flatMap((group) => group.items.map((item) => ({ ...item, groupId: group.id })))
    .filter((item) => {
      if (item.external) return false;
      if (item.groupId === 'editing') {
        // mode 项（navigationKey 名与 EDITING_MODE_ROUTES 的 navigationKey 一一对应）
        const modeEntry = EDITING_MODE_ROUTES.find((entry) => entry.navigationKey === item.id);
        if (modeEntry) return isEditingPage && modeEntry.id === currentEditingMode;
        // editingApi / editingDocs 等非 mode 项，回退到 pathname
        return pathname.startsWith('/vidu-stream/editing');
      }
      return pathname === item.href || pathname.startsWith(`${item.href}/`);
    })
    .sort((left, right) => right.href.length - left.href.length)[0];
  const activeGroupId = activeItem?.groupId;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({
    interaction: activeGroupId === 'interaction',
    editing: activeGroupId === 'editing' || !activeGroupId,
  }));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHome = pathname === '/vidu-stream';
  const isProfile = pathname === '/vidu-stream/profile';

  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((current) => (current[activeGroupId] ? current : { ...current, [activeGroupId]: true }));
  }, [activeGroupId]);

  useEffect(() => {
    if (!hideProductHeader) return;
    const handleOpenMobileMenu = () => setMobileMenuOpen(true);
    window.addEventListener('vidu-stream:open-mobile-menu', handleOpenMobileMenu);
    return () => window.removeEventListener('vidu-stream:open-mobile-menu', handleOpenMobileMenu);
  }, [hideProductHeader]);

  return (
    <>
      {/* 这条 header 的底色是写死的 bg-black/85，不跟随主题，所以 logo 也固定用白色。 */}
      {!hideProductHeader && (
        <header
          className={cn(
            'fixed inset-x-0 top-0 z-50 hidden border-system-white08 border-b bg-black/85 px-3 py-2 backdrop-blur-[24px] sm:block',
            hideProductHeader && 'top-[72px] sm:top-14',
            preserveLiveId && 'sm:!hidden',
          )}
        >
          <div className="flex h-10 items-center gap-3">
            {!hideProductHeader && (
              <LeaveGuardedLink
                confirmLeave={preserveLiveId}
                href="/vidu-stream"
                aria-label={t('product')}
                className="shrink-0"
                onConfirmLeave={handleConfirmLeave}
              >
                <ViduSLogo aria-hidden="true" className="h-5 w-[56px] text-white" />
              </LeaveGuardedLink>
            )}
            <div className="min-w-0 flex-1" />
            <button
              aria-expanded={mobileMenuOpen}
              aria-label="Open menu"
              className="flex size-9 shrink-0 items-center justify-center rounded-10 bg-system-white08 text-system-white80"
              onClick={() => setMobileMenuOpen(true)}
              type="button"
            >
              <BaseLineList className="size-5" />
            </button>
          </div>
        </header>
      )}
      {mobileMenuOpen ? (
        // 底色 bg-system-bg08 会跟随主题翻成浅色，文字必须用同样跟随主题的 token，
        // 写死 text-white 会在亮色下变成白底白字（logo 也会跟着看不见）。
        <div className="fixed inset-0 z-[100] h-dvh w-screen overflow-y-auto bg-system-bg08 px-4 text-[14px] text-system-white sm:pb-[var(--ios-safe-bottom)]">
          <div className="flex h-20 shrink-0 items-center justify-between md:h-16">
            <LeaveGuardedLink
              confirmLeave={preserveLiveId}
              href="/vidu-stream"
              aria-label={t('product')}
              onClick={() => setMobileMenuOpen(false)}
              onConfirmLeave={handleConfirmLeave}
            >
              <ViduSLogo aria-hidden="true" className="h-5 w-[56px] text-system-white" />
            </LeaveGuardedLink>
            <button
              aria-label="Close menu"
              className="flex size-9 items-center justify-center rounded-full bg-system-white12"
              onClick={() => setMobileMenuOpen(false)}
              type="button"
            >
              <BaseLineClose className="size-5" />
            </button>
          </div>
          <LeaveGuardedLink
            aria-current={isHome ? 'page' : undefined}
            className={cn(
              'mb-5 flex min-h-11 items-center rounded-10 px-4 font-semibold text-[15px] text-system-white72 transition-colors hover:bg-system-white08 hover:text-white',
              isHome && 'bg-system-white12 text-white',
            )}
            confirmLeave={preserveLiveId}
            href="/vidu-stream"
            onClick={() => setMobileMenuOpen(false)}
            onConfirmLeave={handleConfirmLeave}
          >
            {t('homeTab')}
          </LeaveGuardedLink>
          {groups.map((group) => (
            <section className="mb-5" key={group.id}>
              <div className="mb-2 px-4 font-semibold text-[15px] text-system-white72">{group.label}</div>
              <div className="ml-3 space-y-1">
                {group.items.map((item) => {
                  const active = item.id === activeItem?.id;
                  const className = cn(
                    'flex min-h-11 items-center rounded-10 px-4 font-normal text-[14px] text-system-white80 transition-colors hover:bg-system-white08 hover:text-white',
                    active && 'bg-system-white12 font-medium text-white',
                  );
                  const isApiItem = item.id === 'interactionApi' || item.id === 'editingApi';
                  // 单路由：mode 项点击写 store 就地切换，不跳转、不改 URL。
                  if (item.modeId && isEditingPage) {
                    const modeId = item.modeId;
                    return (
                      <Fragment key={item.id}>
                        {isApiItem ? (
                          <div aria-hidden="true" className="!my-3 h-px w-full shrink-0 bg-system-white12" />
                        ) : null}
                        <button
                          aria-current={active ? 'page' : undefined}
                          className={className}
                          key={item.id}
                          onClick={() => {
                            reportModeSwitch(modeId);
                            setEditingMode(modeId);
                            syncEditingModeToUrl(modeId);
                            setMobileMenuOpen(false);
                          }}
                          type="button"
                        >
                          {item.label}
                        </button>
                      </Fragment>
                    );
                  }
                  return (
                    <Fragment key={item.id}>
                      {isApiItem ? (
                        <div aria-hidden="true" className="!my-3 h-px w-full shrink-0 bg-system-white12" />
                      ) : null}
                      {item.external ? (
                        <a
                          className={className}
                          href={item.href}
                          key={item.id}
                          onClick={() => setMobileMenuOpen(false)}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <LeaveGuardedLink
                          aria-current={active ? 'page' : undefined}
                          className={className}
                          confirmLeave={preserveLiveId}
                          href={preserveEditingQuery(item.href, preserveLiveId)}
                          key={item.id}
                          onClick={() => setMobileMenuOpen(false)}
                          onConfirmLeave={handleConfirmLeave}
                        >
                          {item.label}
                        </LeaveGuardedLink>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </section>
          ))}
          {userInfo ? (
            <LeaveGuardedLink
              aria-current={isProfile ? 'page' : undefined}
              className={cn(
                'mb-5 flex min-h-11 items-center rounded-10 px-4 font-semibold text-[15px] text-system-white72 transition-colors hover:bg-system-white08 hover:text-white',
                isProfile && 'bg-system-white12 text-white',
              )}
              confirmLeave={preserveLiveId}
              href="/vidu-stream/profile"
              onConfirmLeave={handleConfirmLeave}
            >
              {t('me')}
            </LeaveGuardedLink>
          ) : null}
        </div>
      ) : null}
      <aside
        className={cn(
          'sticky top-0 flex h-dvh w-[240px] shrink-0 flex-col border-system-white08 border-r bg-[rgb(var(--system-bg08-rgb)/0.72)] px-3 py-5 text-system-white backdrop-blur-[40px] sm:hidden',
          fitWorkspaceHeight && 'h-full',
        )}
      >
        <div className={cn('mb-8 px-4', hideProductHeader && 'hidden')}>
          <LeaveGuardedLink
            aria-label={t('product')}
            className="inline-flex max-w-full items-center transition-opacity hover:opacity-80"
            confirmLeave={preserveLiveId}
            href="/vidu-stream"
            onConfirmLeave={handleConfirmLeave}
          >
            <ViduSLogo aria-hidden="true" className="h-8 w-[89px] shrink-0 text-system-white" />
          </LeaveGuardedLink>
        </div>

        <nav aria-label={t('ariaLabel')} className="min-h-0 flex-1 overflow-y-auto">
          <LeaveGuardedLink
            aria-current={isHome ? 'page' : undefined}
            className={cn(
              'mb-5 flex h-11 w-full items-center rounded-8 px-3 font-semibold text-[15px] text-system-white72 transition-colors hover:bg-system-white06 hover:text-white',
              isHome && 'bg-system-white06 text-white',
            )}
            confirmLeave={preserveLiveId}
            href="/vidu-stream"
            onConfirmLeave={handleConfirmLeave}
          >
            {t('homeTab')}
          </LeaveGuardedLink>
          {groups.map((group) => {
            const isOpen = openGroups[group.id] ?? false;
            const isActiveGroup = group.id === activeGroupId;
            return (
              <section className="mb-5" key={group.id}>
                <button
                  aria-expanded={isOpen}
                  className={cn(
                    'flex h-11 w-full items-center justify-between rounded-8 px-3 font-semibold text-[15px] text-system-white72 transition-colors hover:bg-system-white06 hover:text-white',
                    (isOpen || isActiveGroup) && 'text-white',
                    isActiveGroup && 'bg-system-white06',
                  )}
                  onClick={() => setOpenGroups((current) => ({ ...current, [group.id]: !isOpen }))}
                  type="button"
                >
                  <span>{group.label}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'size-3.5 text-system-white48 transition-transform duration-200',
                      isOpen && 'rotate-180',
                    )}
                  />
                </button>
                <div
                  className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-200',
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
                  )}
                >
                  <div className="ml-3 min-h-0 space-y-1 overflow-hidden pt-2">
                    {group.items.map((item) => {
                      const active = item.id === activeItem?.id;
                      const className = cn(linkClassName, active && 'bg-system-white12 font-medium text-white');
                      const isApiItem = item.id === 'interactionApi' || item.id === 'editingApi';
                      // 单路由：mode 项点击写 store 就地切换（aside 为桌面态，无需关菜单）。
                      if (item.modeId && isEditingPage) {
                        const modeId = item.modeId;
                        return (
                          <Fragment key={item.id}>
                            {isApiItem ? (
                              <div aria-hidden="true" className="!my-3 h-px w-full shrink-0 bg-system-white12" />
                            ) : null}
                            <button
                              aria-current={active ? 'page' : undefined}
                              className={className}
                              onClick={() => {
                                reportModeSwitch(modeId);
                                setEditingMode(modeId);
                                syncEditingModeToUrl(modeId);
                              }}
                              type="button"
                            >
                              {item.label}
                            </button>
                          </Fragment>
                        );
                      }
                      return (
                        <Fragment key={item.id}>
                          {isApiItem ? (
                            <div aria-hidden="true" className="!my-3 h-px w-full shrink-0 bg-system-white12" />
                          ) : null}
                          {item.external ? (
                            <a className={className} href={item.href} rel="noopener noreferrer" target="_blank">
                              {item.label}
                            </a>
                          ) : (
                            <LeaveGuardedLink
                              aria-current={active ? 'page' : undefined}
                              className={className}
                              confirmLeave={preserveLiveId}
                              href={preserveEditingQuery(item.href, preserveLiveId)}
                              onConfirmLeave={handleConfirmLeave}
                            >
                              {item.label}
                            </LeaveGuardedLink>
                          )}
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          })}
          {userInfo ? (
            <LeaveGuardedLink
              aria-current={isProfile ? 'page' : undefined}
              className={cn(
                'mb-5 flex h-11 w-full items-center rounded-8 px-3 font-semibold text-[15px] text-system-white72 transition-colors hover:bg-system-white06 hover:text-white',
                isProfile && 'bg-system-white06 text-white',
              )}
              confirmLeave={preserveLiveId}
              href="/vidu-stream/profile"
              onConfirmLeave={handleConfirmLeave}
            >
              {t('me')}
            </LeaveGuardedLink>
          ) : null}
        </nav>
      </aside>
    </>
  );
};
